import { useState } from "react";
import { useCriarVinculo, usePaginaDeConvenios, useSistemas } from "@/api/consultas";
import { useAvisos } from "@/componentes/Avisos";
import { Esqueleto } from "@/componentes/Esqueleto";
import { ModalDeCadastro } from "@/componentes/ModalDeCadastro";
import { Selecao } from "@/componentes/Selecao";
import { useSessao } from "@/sessao/useSessao";
import { EscolhaDeConvenios } from "@/telas/sistemas/EscolhaDeConvenios";
import { textoDaRecusa } from "./recusas";

/*
 * Vincular uma aplicação a partir do cedente, o caminho inverso do `NovoVinculo`: lá o
 * operador escolhe o cedente, aqui a aplicação. O corpo e a rota são os mesmos.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ COM OUTRO INTEGRADOR, ABRE EM "SÓ OS MARCADOS", E NÃO EM "TODOS".        │
 * │                                                                          │
 * │ `null` só é aceito do primeiro integrador (`conferirVinculoCompartilhado`│
 * │ e gatilho BM007): abrir em "Todos" levaria o operador direto para a      │
 * │ recusa. A aba monta este modal só com os vínculos lidos, para o estado   │
 * │ inicial não nascer de uma lista vazia que ainda estava carregando.       │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export function VincularAplicacao({
  pessoaID,
  pessoaNome,
  jaVinculadas,
  aoFechar,
}: {
  pessoaID: string;
  pessoaNome: string;
  /** Fora da lista: vincular de novo é `409 vinculo_existente`. */
  jaVinculadas: ReadonlySet<string>;
  aoFechar: () => void;
}) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  const sistemas = useSistemas(cliente, true);
  const convenios = usePaginaDeConvenios(cliente, { pessoa_id: pessoaID }, true);

  const [sistemaID, setSistemaID] = useState("");
  const [permitidos, setPermitidos] = useState<string[] | null>(jaVinculadas.size > 0 ? [] : null);
  const [erroDaAplicacao, setErroDaAplicacao] = useState<string | undefined>(undefined);
  const criar = useCriarVinculo(cliente, sistemaID);

  const opcoes: (readonly [string, string])[] = [
    ["", "—"],
    ...(sistemas.data?.sistemas ?? [])
      .filter((sistema) => !jaVinculadas.has(sistema.id))
      .map((sistema) => [sistema.id, sistema.nome] as const),
  ];

  function enviar() {
    if (sistemaID === "") {
      setErroDaAplicacao("Escolha a aplicação.");
      return;
    }
    setErroDaAplicacao(undefined);
    // Sem a lista à vista, o operador estaria vinculando o que não viu.
    if (convenios.data === undefined) return;

    criar.mutate(
      // A lista vai SEMPRE, inclusive nula, como em `NovoVinculo`.
      { pessoa_id: pessoaID, convenios_permitidos: permitidos },
      {
        onSuccess: () => {
          avisos.feito("Aplicação vinculada");
          aoFechar();
        },
      },
    );
  }

  const falha = criar.error ?? sistemas.error ?? convenios.error;

  return (
    <ModalDeCadastro
      aberto
      aoFechar={aoFechar}
      titulo="Vincular aplicação"
      descricao={pessoaNome}
      aoEnviar={enviar}
      enviando={criar.isPending}
      erro={falha === null ? undefined : textoDaRecusa(falha)}
      rotuloDeEnvio="Vincular"
    >
      <Selecao
        rotulo="Aplicação"
        opcoes={opcoes}
        value={sistemaID}
        onChange={(evento) => setSistemaID(evento.target.value)}
        erro={erroDaAplicacao}
        autoFocus
      />

      {/*
        Sem os dados, "Nenhum convênio." seria falso: carregando, ou com a leitura
        recusada, a escolha ainda não existe.
      */}
      {convenios.data !== undefined ? (
        <EscolhaDeConvenios
          convenios={convenios.data.convenios}
          valor={permitidos}
          aoMudar={setPermitidos}
        />
      ) : (
        convenios.error === null && <Esqueleto className="h-24" />
      )}
    </ModalDeCadastro>
  );
}
